<template>
    <!-- El layout fuerza padding-top: 0 en su v-content, asi que este wrapper es el
         que deja libre el espacio de la barra superior. Igual que las demas vistas. -->
    <v-content>
    <v-container>
        <v-row>
            <!-- ------------------------------------------------ Mis datos -->
            <v-col cols="12" md="6">
                <v-card class="h-full">
                    <v-card-title class="py-3">
                        <v-icon left color="primary">account_circle</v-icon>
                        Mis datos
                    </v-card-title>
                    <v-divider/>
                    <v-simple-table dense>
                        <tbody>
                        <tr>
                            <td class="etiqueta">Nombre</td>
                            <td>{{ nombre }}</td>
                        </tr>
                        <tr>
                            <td class="etiqueta">Correo</td>
                            <td>{{ datos.email || '-' }}</td>
                        </tr>
                        <tr v-if="datos.position">
                            <td class="etiqueta">Cargo</td>
                            <td>{{ datos.position }}</td>
                        </tr>
                        <tr v-if="datos.area">
                            <td class="etiqueta">Area</td>
                            <td>{{ datos.area }}</td>
                        </tr>
                        <tr>
                            <td class="etiqueta">Perfil de acceso</td>
                            <td>
                                <v-chip x-small color="primary" dark>{{ perfilNombre }}</v-chip>
                            </td>
                        </tr>
                        <tr v-if="ultimoAcceso">
                            <td class="etiqueta">Ultimo acceso</td>
                            <td>{{ ultimoAcceso | moment("dddd D [de] MMMM, HH:mm") }}</td>
                        </tr>
                        </tbody>
                    </v-simple-table>
                    <v-card-text class="caption text--secondary pt-3">
                        Tus datos y tu perfil de acceso los administra el area de sistemas.
                        Si algo no corresponde, pedi el cambio: desde aca no se pueden editar.
                    </v-card-text>
                </v-card>
            </v-col>

            <!-- --------------------------------------- Cambiar contrasenia -->
            <v-col cols="12" md="6">
                <v-card class="h-full">
                    <v-card-title class="py-3">
                        <v-icon left color="primary">lock</v-icon>
                        Cambiar contrasenia
                    </v-card-title>
                    <v-divider/>
                    <v-card-text class="pt-4">
                        <v-alert v-if="claveMsg" :key="claveMsg" :type="claveTipo" dense text class="mb-4">
                            {{ claveMsg }}
                        </v-alert>

                        <v-text-field
                                v-model="actual"
                                label="Contrasenia actual"
                                :type="verActual ? 'text' : 'password'"
                                :append-icon="verActual ? 'visibility_off' : 'visibility'"
                                @click:append="verActual = !verActual"
                                outlined dense
                                autocomplete="current-password"
                                :disabled="guardandoClave"
                        />
                        <v-text-field
                                v-model="nueva"
                                label="Contrasenia nueva"
                                :type="verNueva ? 'text' : 'password'"
                                :append-icon="verNueva ? 'visibility_off' : 'visibility'"
                                @click:append="verNueva = !verNueva"
                                outlined dense
                                autocomplete="new-password"
                                :hint="'Minimo ' + MINIMO + ' caracteres'"
                                persistent-hint
                                :disabled="guardandoClave"
                        />
                        <v-text-field
                                v-model="repetida"
                                label="Repetir la contrasenia nueva"
                                :type="verNueva ? 'text' : 'password'"
                                outlined dense
                                class="mt-4"
                                autocomplete="new-password"
                                :disabled="guardandoClave"
                                @keyup.enter="cambiarClave"
                        />

                        <v-btn
                                color="primary"
                                block
                                class="mt-2"
                                :loading="guardandoClave"
                                :disabled="guardandoClave"
                                @click="cambiarClave"
                        >
                            Cambiar contrasenia
                        </v-btn>

                        <div class="caption text--secondary mt-3">
                            Se pide la contrasenia actual porque asi lo exige Firebase para
                            cambiarla, y porque evita que alguien que encuentre tu sesion abierta
                            pueda dejarte afuera.
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <v-row>
            <!-- ------------------------------------ Que permite mi perfil -->
            <v-col cols="12" md="6">
                <v-card class="h-full">
                    <v-card-title class="py-3">
                        <v-icon left color="primary">vpn_key</v-icon>
                        Que permite mi perfil
                    </v-card-title>
                    <v-divider/>
                    <v-card-text class="pt-3">
                        <!-- El sistema de perfiles es configurable, asi que nadie sabe de
                             memoria que le toca. Verlo escrito evita la mitad de las
                             preguntas de "por que no veo tal pantalla". -->
                        <div v-if="esAdminPanel" class="mb-2">
                            <v-chip small color="primary" dark>
                                <v-icon x-small left>verified_user</v-icon>
                                Acceso total al panel
                            </v-chip>
                            <div class="caption text--secondary mt-2">
                                Tu perfil es de sistema: entra a todas las pantallas y puede
                                hacer todas las acciones.
                            </div>
                        </div>

                        <template v-else>
                            <div class="subtitulo">Pantallas ({{ pantallasVisibles.length }})</div>
                            <div v-if="pantallasVisibles.length === 0" class="caption text--secondary">
                                Tu perfil no tiene ninguna pantalla habilitada.
                            </div>
                            <div class="fichas">
                                <v-chip
                                        v-for="m in pantallasVisibles"
                                        :key="m.clave"
                                        x-small
                                        outlined
                                        class="ficha"
                                >
                                    <v-icon x-small left>{{ m.icono }}</v-icon>
                                    {{ m.nombre }}
                                </v-chip>
                            </div>

                            <div class="subtitulo mt-4">Acciones ({{ accionesVisibles.length }})</div>
                            <div v-if="accionesVisibles.length === 0" class="caption text--secondary">
                                Tu perfil solo tiene acceso de consulta.
                            </div>
                            <ul class="acciones">
                                <li v-for="p in accionesVisibles" :key="p.clave">{{ p.nombre }}</li>
                            </ul>

                            <template v-if="alcanceTexto">
                                <div class="subtitulo mt-4">{{ ALCANCE.titulo }}</div>
                                <div class="body-2">{{ alcanceTexto }}</div>
                            </template>
                        </template>
                    </v-card-text>
                </v-card>
            </v-col>

            <!-- ------------------------------- Ultimos inicios de sesion -->
            <v-col cols="12" md="6">
                <v-card class="h-full">
                    <v-card-title class="py-3">
                        <v-icon left color="primary">history</v-icon>
                        Ultimos inicios de sesion
                        <v-spacer/>
                        <v-btn small text :loading="cargandoSesiones" @click="cargarSesiones">
                            <v-icon small left>refresh</v-icon>
                            Actualizar
                        </v-btn>
                    </v-card-title>
                    <v-divider/>

                    <div v-if="cargandoSesiones" class="pa-6 text-center">
                        <v-progress-circular indeterminate size="26" color="primary"/>
                    </div>

                    <v-alert v-else-if="sesionesMsg" type="warning" dense text class="ma-4">
                        {{ sesionesMsg }}
                    </v-alert>

                    <!-- Estado vacio explicado: el registro arranca con esta version, asi
                         que al principio la tabla va a estar vacia y no es un error. -->
                    <div v-else-if="sesiones.length === 0" class="pa-5 text-center text--secondary">
                        <v-icon size="36" color="grey lighten-1">history_toggle_off</v-icon>
                        <div class="body-2 mt-2">Todavia no hay accesos registrados.</div>
                        <div class="caption mt-1">
                            El registro de accesos empieza a guardarse desde esta version del
                            panel. El tuyo va a aparecer la proxima vez que inicies sesion.
                        </div>
                    </div>

                    <v-simple-table v-else dense>
                        <thead>
                        <tr>
                            <th>Fecha y hora</th>
                            <th>Desde</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="s in sesiones" :key="s.id">
                            <td>{{ aFecha(s.createdAt) | moment("DD/MM/YYYY HH:mm:ss") }}</td>
                            <td class="text--secondary">{{ s.navegador || 'Sin datos' }}</td>
                        </tr>
                        </tbody>
                    </v-simple-table>

                    <v-card-text class="caption text--secondary pt-3">
                        Si ves un acceso que no reconoces, cambia tu contrasenia ahora mismo y
                        avisa al area de sistemas.
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
    </v-content>
</template>

<script>
    import firebase from 'firebase/app'
    import 'firebase/auth'
    import { db } from '@/plugins/db'
    import { MODULOS, PERMISOS, ALCANCE } from '../plugins/permisos'

    // Firebase acepta desde 6 caracteres. Se piden 8 porque este panel controla el
    // acceso a datos de personal y a la evidencia de los recorridos.
    const MINIMO = 8
    const CUANTAS_SESIONES = 12

    export default {
        name: 'Perfil',
        data () {
            return {
                MINIMO,
                ALCANCE,

                actual: '',
                nueva: '',
                repetida: '',
                verActual: false,
                verNueva: false,
                guardandoClave: false,
                claveMsg: '',
                claveTipo: 'info',

                sesiones: [],
                cargandoSesiones: true,
                sesionesMsg: '',

                // Nombres de las dependencias del alcance. No se pueden tomar del
                // store: esa coleccion solo se carga para quien tiene las pantallas de
                // PQR, y a "Mi perfil" entra cualquiera. Se leen a demanda y solo si
                // el perfil tiene alcance acotado.
                nombresDependencias: []
            }
        },
        computed: {
            usuario () {
                return this.$store.getters.user || {}
            },

            // El documento de `users` esta en el store solo si la persona tiene la
            // pantalla de Usuarios; por eso los datos base salen de la sesion.
            datos () {
                return this.usuario
            },

            nombre () {
                return this.usuario.name || this.usuario.email || 'Sin nombre'
            },

            perfil () {
                return this.$store.getters.perfilActivo
            },

            perfilNombre () {
                return this.perfil ? this.perfil.nombre : 'Sin perfil'
            },

            esAdminPanel () {
                return this.$store.getters.esAdminPanel
            },

            ultimoAcceso () {
                // El mas reciente de la lista es el acceso actual.
                if (this.sesiones.length === 0) return null
                return this.aFecha(this.sesiones[0].createdAt)
            },

            pantallasVisibles () {
                // Se usa el getter, no la lista guardada: asi las pantallas
                // obligatorias -las que todo perfil tiene- aparecen igual.
                return MODULOS.filter(m => this.$store.getters.puedeVerModulo(m.clave))
            },

            accionesVisibles () {
                return PERMISOS.filter(p => !p.esAlcance && this.$store.getters.puede(p.clave))
            },

            alcanceTexto () {
                if (!this.perfil) return ''
                // Solo tiene sentido mostrarlo si el perfil trabaja con PQR.
                const acotable = MODULOS.some(m => m.acotable && this.$store.getters.puedeVerModulo(m.clave))
                if (!acotable) return ''

                if (this.$store.getters.puede(ALCANCE.permisoTodas)) return ALCANCE.opcionTodas

                const ids = this.$store.getters.misDependencias
                if (!ids.length) return 'Sin dependencias asignadas todavia.'

                if (this.nombresDependencias.length) return this.nombresDependencias.join(', ')
                return ids.length + ' dependencia(s) asignada(s)'
            }
        },
        methods: {
            aFecha (valor) {
                if (!valor) return null
                if (typeof valor.toDate === 'function') return valor.toDate()
                if (valor instanceof Date) return valor
                if (valor.seconds) return new Date(valor.seconds * 1000)
                return null
            },

            // Se resuelven los nombres una sola vez, y solo los del alcance del perfil:
            // como maximo unos pocos documentos.
            async cargarDependencias () {
                const ids = this.$store.getters.misDependencias
                if (!ids.length) return

                // Si el store ya las tiene -porque la persona tambien entra a PQR- se
                // usan de ahi y no se gasta ninguna lectura.
                const enStore = this.$store.state.dependencia ? (this.$store.state.dependencia.dependencias || []) : []
                if (enStore.length) {
                    this.nombresDependencias = ids
                        .map(id => { const d = enStore.find(x => x.id === id); return d ? d.name : null })
                        .filter(Boolean)
                    if (this.nombresDependencias.length) return
                }

                try {
                    const docs = await Promise.all(
                        ids.slice(0, 20).map(id => db.collection('dependencias').doc(id).get())
                    )
                    this.nombresDependencias = docs
                        .filter(d => d.exists)
                        .map(d => (d.data() || {}).name)
                        .filter(Boolean)
                } catch (e) {
                    // Sin nombres se muestra la cantidad: no vale la pena un error.
                }
            },

            async cargarSesiones () {
                this.cargandoSesiones = true
                this.sesionesMsg = ''
                this.sesiones = []

                const uid = this.usuario.id
                if (!uid) {
                    this.cargandoSesiones = false
                    this.sesionesMsg = 'No se pudo identificar tu sesion.'
                    return
                }

                try {
                    this.sesiones = await this.leerSesiones(uid, true)
                } catch (error) {
                    if (this.faltaIndice(error)) {
                        // El indice compuesto (userId + createdAt) esta declarado en
                        // firestore.indexes.json, pero Firestore tarda en construirlo
                        // despues de desplegarlo. Mientras tanto se trae sin ordenar y
                        // se ordena en memoria: son pocos documentos por persona.
                        try {
                            const todas = await this.leerSesiones(uid, false)
                            this.sesiones = todas
                                .sort((a, b) => this.ms(b.createdAt) - this.ms(a.createdAt))
                                .slice(0, CUANTAS_SESIONES)
                        } catch (segundo) {
                            this.sesionesMsg = this.explicarLectura(segundo)
                        }
                    } else {
                        this.sesionesMsg = this.explicarLectura(error)
                    }
                } finally {
                    this.cargandoSesiones = false
                }
            },

            async leerSesiones (uid, ordenado) {
                let consulta = db.collection('sesiones').where('userId', '==', uid)
                if (ordenado) {
                    consulta = consulta.orderBy('createdAt', 'desc').limit(CUANTAS_SESIONES)
                }
                const snap = await consulta.get()
                return snap.docs.map(d => Object.assign({}, d.data(), { id: d.id }))
            },

            ms (valor) {
                const f = this.aFecha(valor)
                return f ? f.getTime() : 0
            },

            faltaIndice (error) {
                const codigo = error && error.code ? error.code : ''
                return codigo === 'failed-precondition'
            },

            /*
             * Antes esto decia "revisa la conexion" para cualquier fallo, y por lo tanto
             * para el caso mas probable de todos -que las reglas o los indices todavia no
             * esten desplegados- daba una pista equivocada. Un mensaje generico sobre una
             * causa que se puede identificar cuesta una vuelta entera de diagnostico.
             */
            explicarLectura (error) {
                const codigo = error && error.code ? error.code : ''

                if (codigo === 'permission-denied') {
                    return 'El registro de accesos todavia no esta habilitado en la base de datos: '
                        + 'falta desplegar las reglas de Firestore que declaran la coleccion "sesiones". '
                        + 'El resto de la pantalla funciona igual.'
                }
                if (codigo === 'failed-precondition') {
                    return 'Falta el indice de la coleccion "sesiones" (userId + createdAt). '
                        + 'Se declara en firestore.indexes.json y hay que desplegarlo.'
                }
                if (codigo === 'unavailable' || codigo === 'deadline-exceeded') {
                    return 'Sin conexion con la base de datos. Revisa la red e intenta de nuevo.'
                }
                if (codigo === 'unauthenticated') {
                    return 'Tu sesion expiro. Volve a iniciar sesion.'
                }
                // Cualquier otro caso se muestra con su codigo: es mejor un mensaje
                // tecnico que una pista falsa.
                return 'No se pudieron cargar los accesos' + (codigo ? ' (' + codigo + ')' : '') + '.'
            },

            async cambiarClave () {
                if (this.guardandoClave) return
                this.claveMsg = ''

                if (!this.actual) return this.avisar('error', 'Escribi tu contrasenia actual.')
                if (!this.nueva || this.nueva.length < MINIMO) {
                    return this.avisar('error', 'La contrasenia nueva debe tener al menos ' + MINIMO + ' caracteres.')
                }
                if (this.nueva !== this.repetida) {
                    return this.avisar('error', 'Las dos contrasenias nuevas no coinciden.')
                }
                if (this.nueva === this.actual) {
                    return this.avisar('error', 'La contrasenia nueva tiene que ser distinta de la actual.')
                }

                const cuenta = firebase.auth().currentUser
                if (!cuenta || !cuenta.email) {
                    return this.avisar('error', 'No se pudo leer tu sesion. Volve a iniciar sesion e intenta de nuevo.')
                }

                this.guardandoClave = true
                try {
                    // Firebase exige un inicio de sesion reciente para cambiar la
                    // contrasenia; reautenticar cumple el requisito y de paso verifica
                    // que quien esta al teclado sepa la contrasenia actual.
                    const credencial = firebase.auth.EmailAuthProvider.credential(cuenta.email, this.actual)
                    await cuenta.reauthenticateWithCredential(credencial)
                    await cuenta.updatePassword(this.nueva)

                    this.actual = ''
                    this.nueva = ''
                    this.repetida = ''
                    this.avisar('success', 'Contrasenia actualizada. Usala la proxima vez que inicies sesion.')
                } catch (error) {
                    this.avisar('error', this.explicar(error))
                } finally {
                    this.guardandoClave = false
                }
            },

            explicar (error) {
                const codigo = error && error.code ? error.code : ''
                if (codigo === 'auth/wrong-password' || codigo === 'auth/invalid-credential') {
                    return 'La contrasenia actual no es correcta.'
                }
                if (codigo === 'auth/weak-password') {
                    return 'La contrasenia nueva es demasiado debil. Combina letras, numeros y algun simbolo.'
                }
                if (codigo === 'auth/too-many-requests') {
                    return 'Demasiados intentos. Espera unos minutos antes de volver a probar.'
                }
                if (codigo === 'auth/requires-recent-login') {
                    return 'Por seguridad tenes que volver a iniciar sesion antes de cambiar la contrasenia.'
                }
                if (codigo === 'auth/network-request-failed') {
                    return 'Sin conexion. Revisa la red e intenta de nuevo.'
                }
                return 'No se pudo cambiar la contrasenia. Intenta de nuevo.'
            },

            avisar (tipo, texto) {
                this.claveTipo = tipo
                this.claveMsg = texto
            }
        },
        created () {
            this.cargarSesiones()
            this.cargarDependencias()
        }
    }
</script>

<style scoped>
    .h-full { height: 100%; }

    .etiqueta {
        color: rgba(0, 0, 0, 0.6);
        width: 42%;
        white-space: nowrap;
    }

    .subtitulo {
        font-size: 12px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.4px;
        color: rgba(0, 0, 0, 0.55);
        margin-bottom: 6px;
    }

    .fichas {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
    }

    .acciones {
        margin: 0;
        padding-left: 18px;
        font-size: 13px;
        line-height: 1.6;
    }
</style>
